import React, { useState } from "react";
import {
  UserCheck, ShieldAlert, Bell, Save, RotateCcw,
  Clock, Users, AlertTriangle, Eye, Zap,
  Mail, ChevronDown, ChevronUp,
  ToggleLeft, ToggleRight, Info,
} from "lucide-react";
import axios from '../../utils/axiosDash';

// ── INITIAL CONFIG STATE ──────────────────────────────────────────────────

const defaultConfig = {
  reception: {
    waitTime: {
      enabled: true,
      warningThreshold: 5,
      criticalThreshold: 10,
    },
    tailgating: {
      enabled: true,
      sensitivity: "medium",
      alertOnEvery: true,
      cooldownMinutes: 2,
    },
    deskUnattended: {
      enabled: true,
      thresholdMinutes: 5,
      workingHoursOnly: true,
    },
    headCount: {
      enabled: true,
      maxPersons: 10,
    },
  },
  intrusion: {
    unauthorisedEntry: {
      enabled: true,
      sensitivity: "high",
      instantAlert: true,
    },
    afterHours: {
      enabled: true,
      workStart: "08:00",
      workEnd: "18:00",
    },
    occupancyLimit: {
      enabled: true,
      zones: {
        "Server Room": 0,
        "Restricted Zone B": 0,
        "Storage Room": 1,
        "Network Closet": 0,
      },
    },
    perimeterBreach: {
      enabled: true,
      sensitivity: "high",
    },
  },
  global: {
    alertSound: true,
    emailRecipients: "abc@company.com",
    escalationMinutes: 15,
    autoResolveMinutes: 60,
  },
};

// ── SMALL COMPONENTS ──────────────────────────────────────────────────────

const Toggle = ({ value, onChange }) => (
  <button
    onClick={() => onChange(!value)}
    style={{
      background: "none", border: "none", cursor: "pointer",
      padding: 0, display: "flex", alignItems: "center",
    }}
  >
    {value
      ? <ToggleRight size={28} color="#3b82f6" />
      : <ToggleLeft size={28} color="#cbd5e1" />}
  </button>
);

const ChannelBadge = ({ icon: Icon, label, active, onChange }) => (
  <button
    onClick={() => onChange(!active)}
    style={{
      display: "flex", alignItems: "center", gap: 5,
      padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700,
      cursor: "pointer", transition: "all 0.15s",
      border: active ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
      background: active ? "#eff6ff" : "#f8fafc",
      color: active ? "#1d4ed8" : "#94a3b8",
    }}
  >
    <Icon size={11} />
    {label}
  </button>
);

const SensitivitySelector = ({ value, onChange }) => (
  <div style={{ display: "flex", gap: 6 }}>
    {["low", "medium", "high"].map(s => (
      <button
        key={s}
        onClick={() => onChange(s)}
        style={{
          padding: "4px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700,
          cursor: "pointer", textTransform: "capitalize", transition: "all 0.15s",
          border: value === s
            ? s === "high" ? "1px solid #fecaca" : s === "medium" ? "1px solid #fde68a" : "1px solid #bbf7d0"
            : "1px solid #e2e8f0",
          background: value === s
            ? s === "high" ? "#fef2f2" : s === "medium" ? "#fffbeb" : "#f0fdf4"
            : "#f8fafc",
          color: value === s
            ? s === "high" ? "#dc2626" : s === "medium" ? "#d97706" : "#16a34a"
            : "#94a3b8",
        }}
      >
        {s}
      </button>
    ))}
  </div>
);

const NumberInput = ({ value, onChange, min = 0, max = 999, unit = "" }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <input
      type="number" value={value} min={min} max={max}
      onChange={e => onChange(Number(e.target.value))}
      style={{
        width: 64, padding: "5px 8px", borderRadius: 8, fontSize: 13, fontWeight: 700,
        border: "1px solid #e2e8f0", background: "#f8fafc", color: "#0f172a",
        outline: "none", textAlign: "center",
      }}
    />
    {unit && <span style={{ fontSize: 11, color: "#94a3b8" }}>{unit}</span>}
  </div>
);

const TimeInput = ({ value, onChange }) => (
  <input
    type="time" value={value}
    onChange={e => onChange(e.target.value)}
    style={{
      padding: "5px 8px", borderRadius: 8, fontSize: 12, fontWeight: 700,
      border: "1px solid #e2e8f0", background: "#f8fafc", color: "#0f172a",
      outline: "none",
    }}
  />
);

// ── SECTION HEADER ────────────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, title, color, expanded, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "16px 20px", cursor: "pointer",
      borderBottom: expanded ? "1px solid #f1f5f9" : "none",
      transition: "background 0.15s",
    }}
    onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
  >
    <div style={{
      width: 32, height: 32, borderRadius: 9,
      background: color === "#1d4ed8" ? "#eff6ff" : color === "#dc2626" ? "#fef2f2" : "#f0fdf4",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <Icon size={15} color={color} />
    </div>
    <span style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", flex: 1, letterSpacing: "-0.3px" }}>
      {title}
    </span>
    {expanded ? <ChevronUp size={15} color="#94a3b8" /> : <ChevronDown size={15} color="#94a3b8" />}
  </div>
);

// ── RULE ROW ──────────────────────────────────────────────────────────────

const RuleRow = ({ icon: Icon, title, description, enabled, onToggle, children }) => (
  <div style={{
    borderBottom: "1px solid #f1f5f9",
    padding: "14px 20px",
  }}>
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0, marginTop: 2,
        background: enabled ? "#eff6ff" : "#f8fafc",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={14} color={enabled ? "#3b82f6" : "#94a3b8"} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</span>
            {description && (
              <p style={{ fontSize: 11, color: "#94a3b8", margin: "2px 0 0", lineHeight: 1.4 }}>{description}</p>
            )}
          </div>
          <Toggle value={enabled} onChange={onToggle} />
        </div>
        {enabled && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            {children}
          </div>
        )}
      </div>
    </div>
  </div>
);

const ConfigRow = ({ label, children }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "6px 0",
  }}>
    <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{label}</span>
    {children}
  </div>
);

// ── MAIN ──────────────────────────────────────────────────────────────────

export default function ConfigPage() {
  const [config, setConfig] = useState(defaultConfig);
  const [expanded, setExpanded] = useState({ reception: true, intrusion: true, global: true });
  const [saved, setSaved] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const username = user?.username;
  const update = (section, rule, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [rule]: { ...prev[section][rule], [field]: value },
      },
    }));
  };

  const updateGlobal = (field, value) =>
    setConfig(prev => ({ ...prev, global: { ...prev.global, [field]: value } }));

  const handleSave = async () => {
    try {
      const payload = {
        ...config,
        addedBy: username,
        modifiedBy: username,
      };

      const response = await axios.post(
        "/save-tablespace-configure",
        payload
      );

      if (response.data.success) {
        setSaved(true);

        setTimeout(() => {
          setSaved(false);
        }, 2500);
      }
    } catch (error) {
        console.error(
        "Error saving configuration:",
        error
        );
    }
  };

  const handleReset = () => setConfig(defaultConfig);

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const card = (children, style = {}) => (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0",
      borderRadius: 16, overflow: "hidden", marginBottom: 16, ...style,
    }}>
      {children}
    </div>
  );

  const r = config.reception;
  const i = config.intrusion;
  const g = config.global;

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", maxWidth: 860, margin: "0 auto" }}>

      {/* ── HEADER ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "20px 0 20px",
      }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "#3b82f6", marginBottom: 4 }}>
            System Settings
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px", margin: 0 }}>
            Alert Configuration
          </h1>
          <p style={{ fontSize: 12, color: "#94a3b8", margin: "4px 0 0" }}>
            Configure thresholds, sensitivity, and notification channels for each rule
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={handleReset}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 16px", borderRadius: 10, fontSize: 12, fontWeight: 700,
              border: "1px solid #e2e8f0", background: "#f8fafc", color: "#64748b",
              cursor: "pointer",
            }}
          >
            <RotateCcw size={13} /> Reset
          </button>
          <button
            onClick={handleSave}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 20px", borderRadius: 10, fontSize: 12, fontWeight: 700,
              border: saved ? "1px solid #bbf7d0" : "1px solid #bfdbfe",
              background: saved ? "#f0fdf4" : "#eff6ff",
              color: saved ? "#16a34a" : "#1d4ed8",
              cursor: "pointer", transition: "all 0.3s",
            }}
          >
            <Save size={13} />
            {saved ? "Saved!" : "Save changes"}
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 1 · RECEPTION
      ══════════════════════════════════════ */}
      {card(<>
        <SectionHeader icon={UserCheck} title="Reception Area" color="#1d4ed8"
          expanded={expanded.reception} onToggle={() => toggle("reception")} />

        {expanded.reception && <>

          {/* Wait Time */}
          <RuleRow
            icon={Clock} title="Wait time alert"
            description="Trigger when visitor wait time exceeds threshold"
            enabled={r.waitTime.enabled}
            onToggle={v => update("reception", "waitTime", "enabled", v)}
          >
            <ConfigRow label="Warning threshold">
              <NumberInput value={r.waitTime.warningThreshold} unit="min"
                onChange={v => update("reception", "waitTime", "warningThreshold", v)} />
            </ConfigRow>
            <ConfigRow label="Critical threshold">
              <NumberInput value={r.waitTime.criticalThreshold} unit="min"
                onChange={v => update("reception", "waitTime", "criticalThreshold", v)} />
            </ConfigRow>
          </RuleRow>

          {/* Tailgating */}
          <RuleRow
            icon={Users} title="Tailgating detection"
            description="Alert when multiple people pass through a single scan"
            enabled={r.tailgating.enabled}
            onToggle={v => update("reception", "tailgating", "enabled", v)}
          >
            <ConfigRow label="Detection sensitivity">
              <SensitivitySelector value={r.tailgating.sensitivity}
                onChange={v => update("reception", "tailgating", "sensitivity", v)} />
            </ConfigRow>
            <ConfigRow label="Alert cooldown">
              <NumberInput value={r.tailgating.cooldownMinutes} unit="min"
                onChange={v => update("reception", "tailgating", "cooldownMinutes", v)} />
            </ConfigRow>
            <ConfigRow label="Alert on every event">
              <Toggle value={r.tailgating.alertOnEvery}
                onChange={v => update("reception", "tailgating", "alertOnEvery", v)} />
            </ConfigRow>
          </RuleRow>

          {/* Desk Unattended */}
          <RuleRow
            icon={Eye} title="Reception desk unattended"
            description="Alert if no staff detected at reception desk"
            enabled={r.deskUnattended.enabled}
            onToggle={v => update("reception", "deskUnattended", "enabled", v)}
          >
            <ConfigRow label="Unattended threshold">
              <NumberInput value={r.deskUnattended.thresholdMinutes} unit="min"
                onChange={v => update("reception", "deskUnattended", "thresholdMinutes", v)} />
            </ConfigRow>
            <ConfigRow label="Working hours only">
              <Toggle value={r.deskUnattended.workingHoursOnly}
                onChange={v => update("reception", "deskUnattended", "workingHoursOnly", v)} />
            </ConfigRow>
          </RuleRow>

          {/* Head Count */}
          <RuleRow
            icon={Users} title="Waiting area head count"
            description="Alert when number of persons exceeds maximum capacity"
            enabled={r.headCount.enabled}
            onToggle={v => update("reception", "headCount", "enabled", v)}
          >
            <ConfigRow label="Max persons allowed">
              <NumberInput value={r.headCount.maxPersons} unit="persons"
                onChange={v => update("reception", "headCount", "maxPersons", v)} />
            </ConfigRow>
          </RuleRow>

        </>}
      </>)}

      {/* ══════════════════════════════════════
          SECTION 2 · INTRUSION
      ══════════════════════════════════════ */}
      {card(<>
        <SectionHeader icon={ShieldAlert} title="Intrusion Detection" color="#dc2626"
          expanded={expanded.intrusion} onToggle={() => toggle("intrusion")} />

        {expanded.intrusion && <>

          {/* Unauthorised Entry */}
          <RuleRow
            icon={AlertTriangle} title="Unauthorised entry"
            description="Detect persons entering restricted zones without authorisation"
            enabled={i.unauthorisedEntry.enabled}
            onToggle={v => update("intrusion", "unauthorisedEntry", "enabled", v)}
          >
            <ConfigRow label="Detection sensitivity">
              <SensitivitySelector value={i.unauthorisedEntry.sensitivity}
                onChange={v => update("intrusion", "unauthorisedEntry", "sensitivity", v)} />
            </ConfigRow>
            <ConfigRow label="Instant alert (no delay)">
              <Toggle value={i.unauthorisedEntry.instantAlert}
                onChange={v => update("intrusion", "unauthorisedEntry", "instantAlert", v)} />
            </ConfigRow>
          </RuleRow>

          {/* After Hours */}
          <RuleRow
            icon={Clock} title="After-hours presence"
            description="Alert when persons detected outside defined working hours"
            enabled={i.afterHours.enabled}
            onToggle={v => update("intrusion", "afterHours", "enabled", v)}
          >
            <ConfigRow label="Work start">
              <TimeInput value={i.afterHours.workStart}
                onChange={v => update("intrusion", "afterHours", "workStart", v)} />
            </ConfigRow>
            <ConfigRow label="Work end">
              <TimeInput value={i.afterHours.workEnd}
                onChange={v => update("intrusion", "afterHours", "workEnd", v)} />
            </ConfigRow>
          </RuleRow>

          {/* Zone Occupancy Limits */}
          <RuleRow
            icon={Zap} title="Zone occupancy limit"
            description="Set maximum allowed persons per restricted zone"
            enabled={i.occupancyLimit.enabled}
            onToggle={v => update("intrusion", "occupancyLimit", "enabled", v)}
          >
            {Object.entries(i.occupancyLimit.zones).map(([zone, max]) => (
              <ConfigRow key={zone} label={zone}>
                <NumberInput value={max} unit="max"
                  onChange={v => update("intrusion", "occupancyLimit", "zones", {
                    ...i.occupancyLimit.zones, [zone]: v,
                  })} />
              </ConfigRow>
            ))}
          </RuleRow>

          {/* Perimeter Breach */}
          <RuleRow
            icon={ShieldAlert} title="Perimeter breach"
            description="Alert on detection of physical boundary crossing"
            enabled={i.perimeterBreach.enabled}
            onToggle={v => update("intrusion", "perimeterBreach", "enabled", v)}
          >
            <ConfigRow label="Detection sensitivity">
              <SensitivitySelector value={i.perimeterBreach.sensitivity}
                onChange={v => update("intrusion", "perimeterBreach", "sensitivity", v)} />
            </ConfigRow>
          </RuleRow>

        </>}
      </>)}

      {/* ══════════════════════════════════════
          SECTION 3 · GLOBAL SETTINGS
      ══════════════════════════════════════ */}
      {card(<>
        <SectionHeader icon={Bell} title="Global Notification Settings" color="#16a34a"
          expanded={expanded.global} onToggle={() => toggle("global")} />

        {expanded.global && (
          <div style={{ padding: "4px 0 8px" }}>

            {/* Email recipients */}
            <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <Mail size={14} color="#64748b" />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Email recipients</span>
              </div>
              <input
                type="text"
                value={g.emailRecipients}
                onChange={e => updateGlobal("emailRecipients", e.target.value)}
                placeholder="email@company.com, email2@company.com"
                style={{
                  width: "100%", padding: "8px 12px", borderRadius: 8, fontSize: 12,
                  border: "1px solid #e2e8f0", background: "#f8fafc", color: "#0f172a",
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>

            {/* Escalation + Auto-resolve */}
            <div style={{ padding: "12px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <Info size={13} color="#64748b" />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Escalation delay</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <NumberInput value={g.escalationMinutes} unit="min"
                    onChange={v => updateGlobal("escalationMinutes", v)} />
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>before escalating unacknowledged alerts</span>
                </div>
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <RotateCcw size={13} color="#64748b" />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Auto-resolve</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <NumberInput value={g.autoResolveMinutes} unit="min"
                    onChange={v => updateGlobal("autoResolveMinutes", v)} />
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>auto-close if no activity detected</span>
                </div>
              </div>
            </div>

          </div>
        )}
      </>)}

      {/* ── BOTTOM SAVE BAR ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10,
        padding: "16px 0 24px",
      }}>
        <button
          onClick={handleReset}
          style={{
            padding: "10px 20px", borderRadius: 10, fontSize: 12, fontWeight: 700,
            border: "1px solid #e2e8f0", background: "#f8fafc", color: "#64748b",
            cursor: "pointer",
          }}
        >
          Reset to defaults
        </button>
        <button
          onClick={handleSave}
          style={{
            padding: "10px 24px", borderRadius: 10, fontSize: 12, fontWeight: 700,
            border: saved ? "1px solid #bbf7d0" : "1px solid #bfdbfe",
            background: saved ? "#f0fdf4" : "#eff6ff",
            color: saved ? "#16a34a" : "#1d4ed8",
            cursor: "pointer", transition: "all 0.3s",
          }}
        >
          {saved ? "✓ Saved!" : "Save changes"}
        </button>
      </div>

    </div>
  );
}